import { OOHTagConfig } from '../common/tag-selector/tag-selector.component'
import { BaseFilter } from './BaseFilter'

export const TAG_TYPES = {
  OBJECT: 'object',
  ARRAY: 'array',
}

export class MapMode {
  static NEIGHBORHOOD = 'neighborhood'
  static GRID = 'grid'
  static MUNICIPALITIES: 'municipalities'
}

export interface ToolTip {
  label: string
  value: string
}

export interface Territory {
  id: string
  name: string
  bbox: number[]
  region_id: string
  terrunit: number
}

export interface DeliveryConfig {
  poiMapTerrunits: string[]
  mapModes: string[]
  report: {
    active: boolean
    full?: boolean
    lite?: boolean
    canShow?: string[]
  }
  downloadCarto: boolean
  downloadCSV: boolean
  filters: {
    absolutes: BaseFilter[]
    mobility: BaseFilter[]
  }
  variables: Record<string, VariablesConfig>
  density: boolean
  showVisitorsFrom: boolean
  tagGroups?: OOHTagConfig[]
  hasCampaignCreation: boolean
  hasTrafficData: boolean
  hasAllPOIsInPOIMap: boolean
}

export class VariablesConfig {
  tooltip: ToolTip[]
  target: BaseFilter
}

export class Delivery {
  id: string
  parent_id: string
  name: string
  start: string
  finish: string
  type: string
  territory: Territory
  config: DeliveryConfig
  sections: Record<string, any>
  granularity: string
  variablesKey: string

  get mapMode() {
    return (
      (this.config.mapModes && this.config.mapModes[0]) || MapMode.NEIGHBORHOOD
    )
  }

  get filters() {
    return this.config.filters.absolutes
  }

  get filtersMobility() {
    return this.config.filters.mobility
  }

  get target() {
    const variables = this.config.variables[this.variablesKey]
    return variables && variables.target
  }

  get subfilters(): BaseFilter[] {
    const variables = this.config.variables
    const subfilters =
      variables &&
      Object.values(variables)
        .map((v) => v.target.subfilters)
        .reduce((acum, elem) => acum.concat(elem))
    const keys = new Set()
    subfilters.forEach((filter) => keys.add(filter.id))
    const preparedFilters = [] as BaseFilter[]
    keys.forEach((key) => {
      const filtersSameKey = subfilters.filter((filter) => filter.id === key)
      const preparedFilter = filtersSameKey.reduce((bef, el) => {
        const value = [
          ...(bef.value || el.value || []),
          ...(bef.value
            ? Array.isArray(bef.value)
              ? bef.value
              : [bef.value]
            : []
          ).map((val) => {
            const splittedKey = val.split('_')
            return Object.keys(el.values).filter((v) =>
              v.endsWith(splittedKey[splittedKey.length - 1]),
            )
          }),
          ...(el.value
            ? Array.isArray(el.value)
              ? el.value
              : [el.value]
            : []
          ).map((val) => {
            const splittedKey = val.split('_')
            return Object.keys(bef.values).filter((v) =>
              v.endsWith(splittedKey[splittedKey.length - 1]),
            )
          }),
        ]
        return {
          ...el,
          value,
          values: { ...bef.values, ...el.values },
        } as BaseFilter
      })
      preparedFilter.onlyVisits =
        variables.nvisits &&
        variables.nvisits.target.subfilters.find(
          (f) => f.id === preparedFilter.id,
        ) &&
        !variables.nvisitors.target.subfilters.find(
          (f) => f.id === preparedFilter.id,
        )
      preparedFilters.push(preparedFilter)
    })
    return preparedFilters
  }

  get tooltip() {
    const variables = this.config.variables[this.variablesKey]
    return variables && variables.tooltip
  }

  toString() {
    return this.id.toString()
  }

  constructor(data: any = {}) {
    this.id = data.id || '0'
    this.parent_id = data.parent_id || null
    this.name = data.name || ''
    this.start = data.start || ''
    this.finish = data.finish || ''
    this.granularity = data.granularity || 'month'
    this.territory = data.territory || {
      name: 'Madrid',
      id: '28079',
      bbox: [-3.745473, 40.398172, -3.662204, 40.460955],
      region_id: 'ES',
    }
    this.type = data.type
    this.sections = {}
    this.config = {
      poiMapTerrunits: [],
      report: { active: true },
      downloadCarto: true,
      downloadCSV: false,
      filters: {
        absolutes: [],
        mobility: [],
      },
      mapModes: [],
      variables: {},
      density: false,
      showVisitorsFrom: true,
      tagGroups: [],
      hasCampaignCreation: false,
      hasTrafficData: false,
      hasAllPOIsInPOIMap: false,
    }

    if (data.config) {
      if (!data.config.variables) {
        data.config.variables = [
          {
            key: 'nvisitors',
            targets: data.config.targets,
            stats: [],
          },
        ]
      }
      const {
        modes,
        aggregates,
        mobility_map_terrunits,
        filters,
        filters_mobility,
        variables,
        autoinsights,
        charts,
        comparison,
        twin_areas,
        report,
        download_csv,
        download_carto,
        density,
        show_visitors_from,
        tags,
        hasCampaignCreation,
        hasTrafficData,
        hasAllPOIsInPOIMap,
      } = data.config
      const orderedVariables = variables.sort((a) =>
        a.key === 'nvisitors' ? -1 : 1,
      )
      this.config.poiMapTerrunits = mobility_map_terrunits
      this.config.mapModes = modes || []
      // NOTE: This flag isn't a good idea. Sorry person from the future.
      // You should avoid the use of this flag, and instead of you should fix
      // the query in "Trippers" and "Numbers visitors by type" to use the mobility matrix
      // to have the same numbers that appear in "Visitors from..." widgets. Right now, the filters
      // use the absolute table, which isn't a good idea always.
      if (show_visitors_from !== undefined) {
        this.config.showVisitorsFrom = show_visitors_from
      }

      // configuration for the tags panel in OOH deliveries
      if (tags) {
        this.config.tagGroups = Object.keys(tags).map((key) => ({
          label: key,
          tags: tags[key],
        }))
      }

      // configuration for flags that customize OOH behaviour
      this.config.hasCampaignCreation = hasCampaignCreation
      this.config.hasTrafficData = hasTrafficData
      this.config.hasAllPOIsInPOIMap = hasAllPOIsInPOIMap

      this.config.report = report
        ? {
            active: report.active === undefined ? true : report.active,
            full: report.full,
            lite: report.lite,
            canShow: report.can_show,
          }
        : { active: true }
      this.config.density =
        density === undefined || density === null ? false : density
      this.config.downloadCSV =
        download_csv === undefined || download_csv === null
          ? false
          : download_csv
      this.config.downloadCarto =
        download_carto === undefined || download_carto === null
          ? true
          : download_carto
      this.sections = { aggregates, autoinsights, charts, comparison }
      this.sections.twin_areas =
        twin_areas &&
        twin_areas.active &&
        twin_areas.deliveries &&
        twin_areas.deliveries.indexOf(this.id) !== -1

      this.config.filters.absolutes = Array.isArray(filters)
        ? filters.map((f) => new BaseFilter(f))
        : []

      this.config.filters.mobility = Array.isArray(filters_mobility)
        ? filters_mobility.map((f) => new BaseFilter(f))
        : []

      this.variablesKey = orderedVariables[0] && orderedVariables[0].key
      for (const varGroup of orderedVariables) {
        const target = new BaseFilter({
          kind: 'target',
          default: '',
          subfilters: varGroup.targets,
        })
        target.subfilters.forEach((t) => {
          t.value = undefined
        })
        this.config.variables[varGroup.key] = {
          tooltip: varGroup.stats,
          target,
        }
      }
    }
  }
}

export class Vertical {
  id: any
  img: string
  heading: string
  title: string
  slug: string
  description: string
  deliveries: Record<string, Delivery[]>
  delivery: Delivery
  filteredByUserTerritories: boolean
  territories: string[]
  zone: {
    country: string
    city: string
  }

  constructor(data: any = {}) {
    this.id = data.id || 0
    this.img = data.img || ''
    this.heading = data.heading || ''
    this.title = data.title || ''
    this.slug = data.slug || ''
    this.description = data.description || ''
    this.deliveries = {}
    this.delivery = new Delivery()
    this.territories = data.territories || []
    this.zone = data.zone
    this.filteredByUserTerritories = data.filtered_by_user_territories || false
  }

  isOOH() {
    return String(this.id) === 'ooh-politis'
  }
}
